type RequestOptions = { skipAuth?: boolean; silent?: boolean };

export class ApiError extends Error {
  readonly body: unknown;
  readonly status: number;
  constructor(body: unknown, status: number) {
    let msg: string;
    if (typeof body === 'object' && body !== null) {
      const b = body as Record<string, unknown>;
      msg = String(b.error ?? b.detail ?? `HTTP ${status}`);
    } else {
      msg = `HTTP ${status}`;
    }
    super(msg);
    this.body = body;
    this.status = status;
  }
}

// === In-flight request tracking (for global loader) ===

type ActivityListener = (inFlight: number) => void;
const activityListeners = new Set<ActivityListener>();
let inFlight = 0;

function notifyActivity(): void {
  activityListeners.forEach((l) => l(inFlight));
}

export function subscribeApiActivity(cb: ActivityListener): () => void {
  activityListeners.add(cb);
  cb(inFlight);
  return () => {
    activityListeners.delete(cb);
  };
}

let refreshPromise: Promise<void> | null = null;

async function refresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      await request('POST', '/auth/refresh', undefined, { skipAuth: true });
    } catch {
      throw new Error('refresh failed');
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const tracked = !opts.silent;

  const doFetch = async (): Promise<Response> => {
    if (tracked) {
      inFlight++;
      notifyActivity();
    }
    try {
      return await fetch(`/api${path}`, {
        method,
        credentials: 'include',
        headers: body !== undefined
          ? { 'Content-Type': method === 'PATCH' ? 'application/merge-patch+json' : 'application/json' }
          : undefined,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } finally {
      if (tracked) {
        inFlight--;
        notifyActivity();
      }
    }
  };

  let res = await doFetch();
  if (res.status === 401 && !opts.skipAuth) {
    try {
      await refresh();
      res = await doFetch();
    } catch {
      window.location.href = '/login';
      throw new Error('auth expired');
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err, res.status);
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get:    <T>(p: string, opts?: RequestOptions) => request<T>('GET', p, undefined, opts),
  post:   <T>(p: string, b: unknown, opts?: RequestOptions) => request<T>('POST', p, b, opts),
  patch:  <T>(p: string, b: unknown, opts?: RequestOptions) => request<T>('PATCH', p, b, opts),
  delete: <T>(p: string, opts?: RequestOptions) => request<T>('DELETE', p, undefined, opts),
};
